import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import api from '../api';

const FichaTecnicaForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // Estados básicos
  const [nome, setNome] = useState('');
  const [categoria, setCategoria] = useState('');
  const [tipoFicha, setTipoFicha] = useState('produto_final');
  const [modoPreparo, setModoPreparo] = useState('');
  const [mostrarModoPreparo, setMostrarModoPreparo] = useState(false);

  // Estados de rendimento
  const [quantidadePorcoes, setQuantidadePorcoes] = useState('1');
  const [pesoFinalGramas, setPesoFinalGramas] = useState('');
  const [pesoPorcaoGramas, setPesoPorcaoGramas] = useState('');

  // Estados de itens
  const [itens, setItens] = useState([]);
  const [buscaInsumo, setBuscaInsumo] = useState('');
  const [resultadosInsumos, setResultadosInsumos] = useState([]);
  const [buscaEmbalagem, setBuscaEmbalagem] = useState('');
  const [resultadosEmbalagens, setResultadosEmbalagens] = useState([]);

  // Estados de sub-receitas
  const [subReceitas, setSubReceitas] = useState([]);
  const [buscaSubReceita, setBuscaSubReceita] = useState('');
  const [resultadosSubReceitas, setResultadosSubReceitas] = useState([]);

  // Estados de custos
  const [custoTotal, setCustoTotal] = useState(0);
  const [custoUnitario, setCustoUnitario] = useState(0);

  // Estados de precificação
  const [precoVenda, setPrecoVenda] = useState('');
  const [cmvPercentual, setCmvPercentual] = useState('30');
  const [margemLucro, setMargemLucro] = useState('70');
  const [precoManualmenteEditado, setPrecoManualmenteEditado] = useState(false); // NOVO
  const [excluirEmbalagensCMV, setExcluirEmbalagensCMV] = useState(false);
  const [custoEmbalagens, setCustoEmbalagens] = useState(0);
  
  // Estados de delivery
  const [calcularDelivery, setCalcularDelivery] = useState(false);
  const [taxaDelivery, setTaxaDelivery] = useState('27');
  const [modoDelivery, setModoDelivery] = useState('incluir');
  const [custoComDelivery, setCustoComDelivery] = useState(0);
  const [precoFinalComDelivery, setPrecoFinalComDelivery] = useState(0);
  const [taxaDeliveryPorPorcao, setTaxaDeliveryPorPorcao] = useState(0);

  // Estados de UI
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const unidadeRendimento = 'porção';

// ============================================
  // FUNÇÕES DE CÁLCULO
  // ============================================

  const calcularCustos = useCallback(() => {
    let totalInsumos = 0;
    let totalEmbalagens = 0;
    
    // Separar custos de insumos e embalagens
    itens.forEach(item => {
      const custoItem = item.custo_total || 0;
      if (item.tipo === 'embalagem') {
        totalEmbalagens += custoItem;
      } else {
        totalInsumos += custoItem;
      }
    });
    
    // Sub-receitas sempre entram no custo de insumos
    subReceitas.forEach(sub => {
      totalInsumos += sub.custo_total || 0;
    });
    
    // ✅ Se excluir embalagens do CMV, não incluir no custo total
    const custoFinal = excluirEmbalagensCMV ? totalInsumos : totalInsumos + totalEmbalagens;
    
    setCustoTotal(custoFinal);
    setCustoEmbalagens(totalEmbalagens); // ✅ Guardar custo de embalagens separado
    
    const porcoes = parseFloat(quantidadePorcoes) || 1;
    setCustoUnitario(custoFinal / porcoes);
  }, [itens, subReceitas, quantidadePorcoes, excluirEmbalagensCMV]);

  // Handlers de precificação
  const handlePrecoVendaChange = (valor) => {
    setPrecoVenda(valor);
    setPrecoManualmenteEditado(true);
    
    const preco = parseFloat(valor) || 0;
    const custo = parseFloat(custoUnitario) || 0;
    
    // ✅ CORREÇÃO: Quando usuário informa o preço, ele JÁ INCLUI tudo
    // Se embalagens estão excluídas do CMV, precisamos subtrair do preço para calcular CMV
    let precoBase = preco;
    if (excluirEmbalagensCMV && custoEmbalagens > 0) {
      const porcoes = parseFloat(quantidadePorcoes) || 1;
      const custoEmbalagensUnitario = custoEmbalagens / porcoes;
      precoBase = preco - custoEmbalagensUnitario;  // ✅ SUBTRAIR, não adicionar
    }
    
    if (precoBase > 0 && custo > 0) {
      const novoCmv = (custo / precoBase) * 100;
      setCmvPercentual(novoCmv.toFixed(2));
      setMargemLucro((100 - novoCmv).toFixed(2));
    }
  };

  const handleCMVChange = (valor) => {
    setCmvPercentual(valor);
    setPrecoManualmenteEditado(false);
    
    const cmv = parseFloat(valor) || 0;
    const custo = parseFloat(custoUnitario) || 0;
    
    if (cmv > 0 && cmv < 100 && custo > 0) {
      const precoBase = custo / (cmv / 100);
      
      // ✅ AQUI SIM: Quando calcula pelo CMV, adiciona embalagens ao preço
      let precoFinal = precoBase;
      if (excluirEmbalagensCMV && custoEmbalagens > 0) {
        const porcoes = parseFloat(quantidadePorcoes) || 1;
        const custoEmbalagensUnitario = custoEmbalagens / porcoes;
        precoFinal = precoBase + custoEmbalagensUnitario;
      }
      
      setPrecoVenda(precoFinal.toFixed(2));
      setMargemLucro((100 - cmv).toFixed(2));
    }
  };
  
  const calcularDeliveryValues = useCallback(() => {
    if (!calcularDelivery || !taxaDelivery) {
      setCustoComDelivery(custoUnitario);
      setPrecoFinalComDelivery(parseFloat(precoVenda) || 0);
      setTaxaDeliveryPorPorcao(0);  // ✅ ADICIONAR
      return;
    }

    const taxaPercent = parseFloat(taxaDelivery) || 0;
    const precoBase = parseFloat(precoVenda) || 0;
    const porcoes = parseFloat(quantidadePorcoes) || 1;
    
    // Taxa é calculada sobre o preço unitário
    const taxaPorPorcao = precoBase * (taxaPercent / 100);
    setTaxaDeliveryPorPorcao(taxaPorPorcao);  // ✅ SALVAR NO ESTADO
    
    if (modoDelivery === 'incluir') {
      const custoComTaxa = custoUnitario + taxaPorPorcao;
      setCustoComDelivery(custoComTaxa);
      
      const cmv = parseFloat(cmvPercentual) || 30;
      if (cmv > 0 && cmv < 100) {
        let novoPreco = custoComTaxa / (cmv / 100);
        
        if (excluirEmbalagensCMV && custoEmbalagens > 0) {
          const custoEmbalagensUnitario = custoEmbalagens / porcoes;
          novoPreco += custoEmbalagensUnitario;
        }
        
        setPrecoFinalComDelivery(novoPreco);
      }
    } else {
      setCustoComDelivery(custoUnitario);
      setPrecoFinalComDelivery(precoBase + taxaPorPorcao);
    }
  }, [calcularDelivery, taxaDelivery, modoDelivery, custoUnitario, precoVenda, cmvPercentual, quantidadePorcoes, excluirEmbalagensCMV, custoEmbalagens]);

  // Helpers para warnings
  const getPrecoIdeal = () => {
    // SEMPRE usar CMV 30% para o preço ideal
    let precoBase = custoUnitario / 0.30;
    
    // ✅ Se excluir embalagens, adicionar ao preço ideal
    if (excluirEmbalagensCMV) {
      const porcoes = parseFloat(quantidadePorcoes) || 1;
      const custoEmbalagensUnitario = custoEmbalagens / porcoes;
      precoBase += custoEmbalagensUnitario;
    }
    
    return precoBase;
  };

  const getCMVStatus = () => {
    const cmv = parseFloat(cmvPercentual) || 0;
    if (cmv < 25) return { text: 'CMV Excelente', color: 'success' };
    if (cmv <= 35) return { text: 'CMV Ideal', color: 'success' };
    if (cmv <= 45) return { text: 'CMV Aceitável', color: 'warning' };
    return { text: 'CMV Alto', color: 'danger' };
  };
  
  // ============================================
  // FUNÇÕES DE CARREGAMENTO
  // ============================================

  const carregarCategorias = useCallback(async () => {
    try {
      const response = await api.get('/fichas/api/fichas-tecnicas/categorias');
      if (response.data.success) {
        setCategorias(response.data.categorias);
      }
    } catch (err) {
      console.error('Erro ao carregar categorias:', err);
    }
  }, []);

  const carregarFicha = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/fichas/api/fichas-tecnicas/${id}`);
      
      if (response.data.success) {
        const ficha = response.data.ficha;
        
        setNome(ficha.nome);
        setCategoria(ficha.categoria || '');
        setTipoFicha(ficha.tipo_ficha || 'produto_final');
        setModoPreparo(ficha.modo_preparo || '');
        setMostrarModoPreparo(Boolean(ficha.modo_preparo && ficha.modo_preparo.trim()));
        setQuantidadePorcoes(ficha.porcoes || ficha.rendimento || 1);
        setPesoFinalGramas(ficha.peso_total || '');
        setExcluirEmbalagensCMV(ficha.excluir_embalagens_cmv || false);  // ✅ ADICIONAR
        
        if (ficha.peso_total && ficha.porcoes) {
          setPesoPorcaoGramas((ficha.peso_total / ficha.porcoes).toFixed(0));
        }
        
        const itensCarregados = ficha.itens.map(item => ({
          id: item.id || Date.now() + Math.random(),
          produto_id: item.produto_id || null,
          embalagem_id: item.embalagem_id || null,
          produto_sem_controle_id: item.produto_sem_controle_id || null,
          nome: item.nome,
          quantidade: item.quantidade,
          unidade_medida: item.unidade_medida,
          unidade_base: item.unidade_peso || 'unidade',
          custo_unitario: item.custo_unitario,  // ← ESTE VALOR AQUI
          custo_total: item.custo_total,
          unidades_disponiveis: item.unidades_disponiveis || [],
          tipo: item.tipo || 'insumo'
        }));

        // ✅ ADICIONE ESTE CONSOLE.LOG LOGO DEPOIS
        console.log('📦 Itens carregados:', itensCarregados.map(i => ({
          nome: i.nome,
          custo_unitario: i.custo_unitario,
          unidade_base: i.unidade_base,
          unidade_medida: i.unidade_medida
        })));

        setItens(itensCarregados);

        const subReceitasCarregadas = (ficha.sub_receitas || []).map(sub => ({
          id: sub.id || Date.now() + Math.random(),
          ficha_tecnica_id: sub.ficha_tecnica_id || sub.id,
          nome: sub.nome,
          quantidade: sub.quantidade,
          unidade_medida: sub.unidade_medida || 'g',
          custo_unitario: sub.custo_unitario,
          custo_total: sub.custo_total,
          peso_total: sub.peso_total,
          unidades_disponiveis: sub.unidades_disponiveis || [],
          tipo: 'sub_receita'
        }));
        setSubReceitas(subReceitasCarregadas);

        setPrecoVenda(ficha.preco_venda_sugerido || '');
        setCmvPercentual(ficha.cmv_percentual || 30);
        setMargemLucro(ficha.margem_lucro_percentual || 70);
      }
    } catch (err) {
      console.error('Erro ao carregar ficha:', err);
      setError('Erro ao carregar ficha técnica');
    } finally {
      setLoading(false);
    }
  }, [id]);

  // ============================================
  // FUNÇÕES DE BUSCA
  // ============================================

  const buscarInsumos = async (termo) => {
    try {
      const response = await api.get('/fichas/api/fichas-tecnicas/produtos/insumos', {
        params: { busca: termo }
      });
      setResultadosInsumos(response.data.produtos || []);
    } catch (err) {
      console.error('Erro ao buscar insumos:', err);
    }
  };

  const buscarEmbalagens = async (termo) => {
    try {
      const response = await api.get('/fichas/api/fichas-tecnicas/buscar-embalagens', {
        params: { termo }
      });
      setResultadosEmbalagens(response.data.produtos || []);
    } catch (err) {
      console.error('Erro ao buscar embalagens:', err);
    }
  };

  const buscarSubReceitas = useCallback(async (termo) => {
    try {
      const response = await api.get('/fichas/api/fichas-tecnicas/buscar-sub-receitas', {
        params: { termo, ficha_pai_id: id }
      });
      setResultadosSubReceitas(response.data.fichas || []);
    } catch (err) {
      console.error('Erro ao buscar sub-receitas:', err);
    }
  }, [id]);

  // ============================================
  // EFEITOS
  // ============================================

  useEffect(() => {
    carregarCategorias();
    if (isEditMode) {
      carregarFicha();
    }
  }, [isEditMode, carregarCategorias, carregarFicha]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (buscaInsumo && buscaInsumo.length >= 2) {
        buscarInsumos(buscaInsumo);
      } else {
        setResultadosInsumos([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [buscaInsumo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (buscaEmbalagem && buscaEmbalagem.length >= 2) {
        buscarEmbalagens(buscaEmbalagem);
      } else {
        setResultadosEmbalagens([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [buscaEmbalagem]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (buscaSubReceita && buscaSubReceita.length >= 2) {
        buscarSubReceitas(buscaSubReceita);
      } else {
        setResultadosSubReceitas([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [buscaSubReceita, buscarSubReceitas]); // ✅ Agora está correto

  useEffect(() => {
    calcularCustos();
  }, [calcularCustos]);

  // Calcular preço inicial quando custo unitário muda (mantém CMV 30%)
  useEffect(() => {
    if (custoUnitario > 0 && !precoManualmenteEditado && !id) {  // ✅ SÓ RODAR EM CRIAÇÃO
      const cmv = parseFloat(cmvPercentual) || 30;
      const precoBase = custoUnitario / (cmv / 100);
      
      // ✅ Se embalagens excluídas, adicionar ao preço
      let precoFinal = precoBase;
      if (excluirEmbalagensCMV && custoEmbalagens > 0) {
        const porcoes = parseFloat(quantidadePorcoes) || 1;
        const custoEmbalagensUnitario = custoEmbalagens / porcoes;
        precoFinal = precoBase + custoEmbalagensUnitario;
      }
      
      setPrecoVenda(precoFinal.toFixed(2));
      setMargemLucro((100 - cmv).toFixed(2));
    }
  }, [custoUnitario, cmvPercentual, precoManualmenteEditado, excluirEmbalagensCMV, custoEmbalagens, quantidadePorcoes, id]);

  useEffect(() => {
    calcularDeliveryValues();
  }, [calcularDeliveryValues]);

  // ============================================
  // FUNÇÕES DE ITENS
  // ============================================

const adicionarItem = (produto, tipo = 'insumo') => {
  let unidadePadrao = 'unidade';
  if (tipo === 'insumo' && produto.unidades_disponiveis) {
    const unidadeDecimal = produto.unidades_disponiveis.find(u => 
      u.valor === 'g' || u.valor === 'ml'
    );
    unidadePadrao = unidadeDecimal ? unidadeDecimal.valor : produto.unidades_disponiveis[0]?.valor || 'unidade';
  } else if (tipo === 'embalagem') {
    unidadePadrao = 'unidade';
  }

  // ✅ DETECTAR TIPO DE PRODUTO CORRETAMENTE
  const tipoOrigem = produto.tipo_origem || 'produto';
  const isProdutoSemControle = tipoOrigem === 'sem_controle';

  const novoItem = {
    id: Date.now(),
    produto_id: tipo === 'insumo' && !isProdutoSemControle ? produto.id : null,  // ✅ CORRIGIDO
    produto_sem_controle_id: isProdutoSemControle ? produto.id : null,  // ✅ NOVO
    embalagem_id: tipo === 'embalagem' ? produto.id : null,
    nome: produto.nome,
    quantidade: tipo === 'embalagem' ? 1 : '',
    unidade_medida: unidadePadrao,
    unidade_base: produto.unidade_peso || 'unidade',
    custo_unitario: produto.custo_medio || 0,
    custo_total: tipo === 'embalagem' ? (produto.custo_medio || 0) : 0,
    unidades_disponiveis: produto.unidades_disponiveis || [],
    tipo,
    tipo_origem: tipoOrigem  // ✅ GUARDAR TIPO ORIGEM
  };
  
  console.log('🔍 Item adicionado:', {
    nome: novoItem.nome,
    tipo_origem: tipoOrigem,
    produto_id: novoItem.produto_id,
    produto_sem_controle_id: novoItem.produto_sem_controle_id
  });
  
  setItens([...itens, novoItem]);
  setBuscaInsumo('');
  setBuscaEmbalagem('');
  setResultadosInsumos([]);
  setResultadosEmbalagens([]);
};

  const atualizarItem = (itemId, campo, valor) => {
    setItens(itens.map(item => {
      if (item.id === itemId) {
        const itemAtualizado = { ...item, [campo]: valor };
        
        if (campo === 'quantidade' || campo === 'unidade_medida') {
          const qtd = parseFloat(campo === 'quantidade' ? valor : item.quantidade) || 0;
          const unidade = campo === 'unidade_medida' ? valor : item.unidade_medida;
          
          // ✅ O custo_unitario já vem convertido para a menor unidade pelo backend
          // Então multiplicamos direto pela quantidade
          itemAtualizado.custo_total = qtd * item.custo_unitario;
        }
        
        return itemAtualizado;
      }
      return item;
    }));
  };

  const removerItem = (itemId) => {
    setItens(itens.filter(item => item.id !== itemId));
  };

  const handleDragEnd = (result) => {
  if (!result.destination) return;

  const items = Array.from(itens);
  const [reorderedItem] = items.splice(result.source.index, 1);
  items.splice(result.destination.index, 0, reorderedItem);

  setItens(items);
  };

  // ============================================
  // FUNÇÕES DE SUB-RECEITAS
  // ============================================

  const adicionarSubReceita = (ficha) => {
    const novaSubReceita = {
      id: Date.now(),
      ficha_tecnica_id: ficha.id,
      nome: ficha.nome,
      quantidade: 1,
      unidade_medida: ficha.unidade_rendimento || 'porção',  // ← ADICIONAR
      custo_unitario: ficha.custo_unitario,
      custo_total: ficha.custo_unitario,
      peso_total: ficha.peso_total || null,  // ← ADICIONAR
      unidades_disponiveis: ficha.unidades_disponiveis || [],  // ← ADICIONAR
      tipo: 'sub_receita'
    };
    
    setSubReceitas([...subReceitas, novaSubReceita]);
    setBuscaSubReceita('');
    setResultadosSubReceitas([]);
  };

    const atualizarSubReceita = (subId, campo, valor) => {
      setSubReceitas(subReceitas.map(sub => {
        if (sub.id === subId) {
          const subAtualizada = { ...sub, [campo]: valor };
          
          // Se mudou quantidade OU unidade, recalcular
          if (campo === 'quantidade' || campo === 'unidade_medida') {
            const qtd = parseFloat(campo === 'quantidade' ? valor : sub.quantidade) || 0;
            const unidade = campo === 'unidade_medida' ? valor : sub.unidade_medida;
            
            let qtdEmPorcoes = qtd;
            
            // ✅ Converter para porções SOMENTE se não for porção
            if (unidade === 'g' && sub.peso_total) {
              qtdEmPorcoes = qtd / sub.peso_total;
            } else if (unidade === 'kg' && sub.peso_total) {
              qtdEmPorcoes = (qtd * 1000) / sub.peso_total;
            }
            // Se unidade === 'porção', qtdEmPorcoes já é qtd (não converte)
            
            // ✅ Guarda a quantidade ORIGINAL (não convertida)
            subAtualizada.quantidade = qtd;
            subAtualizada.unidade_medida = unidade;
            
            // ✅ Calcula custo usando quantidade em porções
            const custoUnitario = parseFloat(sub.custo_unitario) || 0;
            subAtualizada.custo_total = qtdEmPorcoes * custoUnitario;
            
            console.log('🔍 Cálculo sub-receita:', {
              nome: sub.nome,
              quantidade_digitada: qtd,
              unidade: unidade,
              qtd_em_porcoes: qtdEmPorcoes,
              custo_unitario: custoUnitario,
              custo_total: subAtualizada.custo_total
            });
          }
          
          return subAtualizada;
        }
        return sub;
      }));
    };

  const removerSubReceita = (subId) => {
    setSubReceitas(subReceitas.filter(sub => sub.id !== subId));
  };

  // ============================================
  // FUNÇÕES DE RENDIMENTO
  // ============================================

  const handleQuantidadePorcoesChange = (valor) => {
    setQuantidadePorcoes(valor);
    if (pesoFinalGramas && valor) {
      const pesoPorcao = parseFloat(pesoFinalGramas) / parseFloat(valor);
      setPesoPorcaoGramas(pesoPorcao.toFixed(0));
    }
  };

  const handlePesoFinalChange = (valor) => {
    setPesoFinalGramas(valor);
    if (quantidadePorcoes && valor) {
      const pesoPorcao = parseFloat(valor) / parseFloat(quantidadePorcoes);
      setPesoPorcaoGramas(pesoPorcao.toFixed(0));
    }
  };

  // ============================================
  // SUBMIT
  // ============================================

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nome.trim()) {
      setError('Nome é obrigatório');
      return;
    }
    
    if (itens.length === 0 && subReceitas.length === 0) {
      setError('Adicione pelo menos um insumo, embalagem ou sub-receita');
      return;
    }
    
    const dados = {
      nome,
      categoria,
      tipo_ficha: tipoFicha,
      rendimento: parseFloat(quantidadePorcoes),
      unidade_rendimento: unidadeRendimento,
      peso_total: pesoFinalGramas ? parseFloat(pesoFinalGramas) : null,
      porcoes: quantidadePorcoes ? parseInt(quantidadePorcoes) : null,
      modo_preparo: modoPreparo,
      excluir_embalagens_cmv: excluirEmbalagensCMV,  // ✅ ADICIONAR
      itens: itens.map(item => {
        // ✅ CORRIGIR AQUI
        const itemData = {
          quantidade: parseFloat(item.quantidade),
          unidade_medida: item.unidade_medida,
          observacoes: ''
        };

        // ✅ DETERMINAR TIPO E ID CORRETO
        if (item.tipo === 'embalagem') {
          itemData.tipo_origem = 'embalagem';
          itemData.embalagem_id = item.embalagem_id;
          itemData.produto_id = null;
          itemData.produto_sem_controle_id = null;
        } else if (item.produto_sem_controle_id) {
          // Produto sem controle
          itemData.tipo_origem = 'sem_controle';
          itemData.produto_id = item.produto_sem_controle_id;  // ✅ ENVIAR O ID AQUI
          itemData.produto_sem_controle_id = null;  // Backend usa produto_id para identificar
          itemData.embalagem_id = null;
        } else {
          // Produto normal
          itemData.tipo_origem = 'produto';
          itemData.produto_id = item.produto_id;
          itemData.produto_sem_controle_id = null;
          itemData.embalagem_id = null;
        }

        console.log('🔍 Enviando item:', {
          nome: item.nome,
          tipo_origem: itemData.tipo_origem,
          produto_id: itemData.produto_id
        });

        return itemData;
      }),

      sub_receitas: subReceitas.map(sub => {
        let quantidadeFinal = parseFloat(sub.quantidade);
        
        console.log('🔍 Sub-receita ANTES conversão:', {
          nome: sub.nome,
          quantidade_original: sub.quantidade,
          unidade: sub.unidade_medida,
          peso_total: sub.peso_total
        });
        
        // Converter para porções antes de salvar
        if (sub.unidade_medida === 'g' && sub.peso_total) {
          quantidadeFinal = quantidadeFinal / sub.peso_total;
        } else if (sub.unidade_medida === 'kg' && sub.peso_total) {
          quantidadeFinal = (quantidadeFinal * 1000) / sub.peso_total;
        }
        
        console.log('🔍 Sub-receita DEPOIS conversão:', {
          nome: sub.nome,
          quantidade_final: quantidadeFinal
        });
        
        return {
          ficha_tecnica_id: sub.ficha_tecnica_id,
          quantidade: quantidadeFinal,
          unidade_medida: sub.unidade_medida,
          observacoes: ''
        };
      }),
      preco_venda: tipoFicha === 'produto_final' ? (precoVenda ? parseFloat(precoVenda) : 0) : 0,
      cmv_percentual: tipoFicha === 'produto_final' ? parseFloat(cmvPercentual) : 0,
      margem_lucro: tipoFicha === 'produto_final' ? parseFloat(margemLucro) : 0
    };
    
    console.log('📤 Dados completos enviados:', dados);
    
    try {
      setLoading(true);
      setError('');
      
      if (isEditMode) {
        await api.put(`/fichas/api/fichas-tecnicas/${id}`, dados);
      } else {
        await api.post('/fichas/api/fichas-tecnicas', dados);
      }
      
      navigate('/fichas-tecnicas');
    } catch (err) {
      console.error('Erro ao salvar:', err);
      setError(err.response?.data?.message || 'Erro ao salvar ficha técnica');
    } finally {
      setLoading(false);
    }
  };
  
  // ============================================
  // UTILITÁRIOS
  // ============================================

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor || 0);
  };

  // ============================================
  // RENDER
  // ============================================

  if (loading && isEditMode) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-xl-10">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">
                {isEditMode ? '✏️ Editar Ficha Técnica' : '➕ Nova Ficha Técnica'}
              </h2>
              <p className="text-muted mb-0">
                {isEditMode ? 'Atualize as informações da receita' : 'Crie uma nova ficha técnica para suas receitas'}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate('/fichas-tecnicas')}
            >
              ← Voltar
            </button>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button type="button" className="btn-close" onClick={() => setError('')}></button>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Informações Básicas */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">📝 Informações Básicas</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="form-label fw-bold">Tipo de Ficha *</label>
                    <div className="btn-group w-100" role="group">
                      <input
                        type="radio"
                        className="btn-check"
                        name="tipo_ficha"
                        id="tipo_preparacao_base"
                        value="preparacao_base"
                        checked={tipoFicha === 'preparacao_base'}
                        onChange={(e) => setTipoFicha(e.target.value)}
                      />
                      <label className="btn btn-outline-warning" htmlFor="tipo_preparacao_base">
                        🧈 Preparação Base
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="tipo_ficha"
                        id="tipo_produto_final"
                        value="produto_final"
                        checked={tipoFicha === 'produto_final'}
                        onChange={(e) => setTipoFicha(e.target.value)}
                      />
                      <label className="btn btn-outline-success" htmlFor="tipo_produto_final">
                        🎂 Produto Final
                      </label>
                    </div>
                    <small className="text-muted d-block mt-2">
                      {tipoFicha === 'preparacao_base' 
                        ? '💡 Preparações Base (buttercream, ganache, recheios) não têm preço de venda e podem ser usadas como ingredientes em outras receitas.'
                        : '💡 Produtos Finais (bolos, tortas para venda) incluem precificação e podem usar Preparações Base como ingredientes.'}
                    </small>
                  </div>

                  <div className="col-md-8">
                    <label className="form-label">Nome da Receita *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      placeholder="Ex: Bolo de Chocolate, Buttercream Suíço"
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Categoria</label>
                    <select
                      className="form-select"
                      value={categoria}
                      onChange={(e) => setCategoria(e.target.value)}
                    >
                      <option value="">Selecione...</option>
                      {categorias.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Rendimento */}
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">⚖️ Rendimento</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label">Quantidade de Porções *</label>
                    <input
                      type="number"
                      className="form-control"
                      value={quantidadePorcoes}
                      onChange={(e) => handleQuantidadePorcoesChange(e.target.value)}
                      min="1"
                      step="1"
                      required
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Peso Final Total (g)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={pesoFinalGramas}
                      onChange={(e) => handlePesoFinalChange(e.target.value)}
                      min="0"
                      step="1"
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">Peso por Porção (g)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={pesoPorcaoGramas}
                      readOnly
                      placeholder="Calculado automaticamente"
                      style={{backgroundColor: '#f8f9fa'}}
                    />
                  </div>
                </div>

                {pesoFinalGramas && quantidadePorcoes && (
                  <div className="alert alert-info mt-3 mb-0">
                    <small>
                      📊 {quantidadePorcoes} {quantidadePorcoes === '1' ? 'porção' : 'porções'} × {pesoPorcaoGramas}g = {pesoFinalGramas}g total
                    </small>
                  </div>
                )}
              </div>
            </div>

            {/* Insumos */}
            <div className="card mb-4">
              <div className="card-header bg-primary text-white">
                <h5 className="mb-0">🥄 Insumos ({itens.filter(i => i.tipo === 'insumo').length})</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Buscar Insumo</label>
                  <input
                    type="text"
                    className="form-control"
                    value={buscaInsumo}
                    onChange={(e) => setBuscaInsumo(e.target.value)}
                    placeholder="Digite o nome do ingrediente..."
                  />
                  
                  {resultadosInsumos.length > 0 && (
                    <div className="list-group mt-2" style={{maxHeight: '200px', overflowY: 'auto'}}>
                      {resultadosInsumos.map(produto => (
                        <button
                          key={produto.id}
                          type="button"
                          className="list-group-item list-group-item-action"
                          onClick={() => adicionarItem(produto, 'insumo')}
                        >
                          <div className="d-flex justify-content-between">
                            <span>{produto.nome}</span>
                            <span className="text-muted">{formatarMoeda(produto.custo_medio)}/{produto.unidade_peso}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {itens.filter(i => i.tipo === 'insumo').length > 0 ? (
                  <DragDropContext onDragEnd={handleDragEnd}>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover align-middle">
                        <thead className="table-light">
                          <tr>
                            <th style={{width: '5%'}}></th>
                            <th style={{width: '35%'}}>Ingrediente</th>
                            <th style={{width: '15%'}} className="text-center">Quantidade</th>
                            <th style={{width: '15%'}} className="text-center">Unidade</th>
                            <th style={{width: '10%'}} className="text-end">Custo Unit.</th>
                            <th style={{width: '15%'}} className="text-end">Subtotal</th>
                            <th style={{width: '5%'}}></th>
                          </tr>
                        </thead>
                        <Droppable droppableId="insumos">
                          {(provided) => (
                            <tbody {...provided.droppableProps} ref={provided.innerRef}>
                              {itens.filter(i => i.tipo === 'insumo').map((item, index) => (
                                <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                                  {(provided, snapshot) => (
                                    <tr 
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      style={{
                                        ...provided.draggableProps.style,
                                        backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'white'
                                      }}
                                    >
                                      <td {...provided.dragHandleProps} style={{cursor: 'grab'}}>
                                        <span style={{fontSize: '18px'}}>⋮⋮</span>
                                      </td>
                                      <td>
                                        <strong>{item.nome}</strong>
                                        {item.observacoes && (
                                          <small className="d-block text-muted">{item.observacoes}</small>
                                        )}
                                      </td>
                                      <td>
                                        <input
                                          type="number"
                                          className="form-control form-control-sm text-center"
                                          value={item.quantidade}
                                          onChange={(e) => atualizarItem(item.id, 'quantidade', e.target.value)}
                                          min="0"
                                          step="0.01"
                                        />
                                      </td>
                                      <td>
                                        <select
                                          className="form-select form-select-sm"
                                          value={item.unidade_medida}
                                          onChange={(e) => atualizarItem(item.id, 'unidade_medida', e.target.value)}
                                        >
                                          {item.unidades_disponiveis?.map(u => (
                                            <option key={u.valor} value={u.valor}>{u.label}</option>
                                          ))}
                                        </select>
                                      </td>
                                      <td className="text-end">
                                        <small>{formatarMoeda(item.custo_unitario)}</small>
                                      </td>
                                      <td className="text-end">
                                        <strong>{formatarMoeda(item.custo_total)}</strong>
                                      </td>
                                      <td className="text-center">
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() => removerItem(item.id)}
                                        >
                                          ×
                                        </button>
                                      </td>
                                    </tr>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </tbody>
                          )}
                        </Droppable>
                        <tfoot>
                          <tr className="table-light fw-bold">
                            <td></td>
                            <td colSpan="4" className="text-end">Subtotal Insumos:</td>
                            <td className="text-end text-primary">
                              {formatarMoeda(itens.filter(i => i.tipo === 'insumo').reduce((sum, i) => sum + (i.custo_total || 0), 0))}
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </DragDropContext>
                ) : (
                  <div className="alert alert-info mb-0">
                    Nenhum insumo adicionado. Use a busca acima para adicionar ingredientes.
                  </div>
                )}
                </div>
            </div>

            {/* Sub-Receitas - SOMENTE PARA PRODUTO FINAL */}
              <div className="card mb-4 border-warning">
                <div className="card-header bg-warning bg-opacity-10">
                  <h5 className="mb-0">🍰 Preparações Base / Sub-Receitas ({subReceitas.length})</h5>
                </div>
                <div className="card-body">
                  <div className="alert alert-info">
                    <small>
                      💡 Use preparações base (buttercream, ganache, recheios) já cadastradas como ingredientes nesta receita.
                    </small>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Buscar Preparação Base</label>
                    <input
                      type="text"
                      className="form-control"
                      value={buscaSubReceita}
                      onChange={(e) => setBuscaSubReceita(e.target.value)}
                      placeholder="Digite o nome da preparação..."
                    />
                    
                    {resultadosSubReceitas.length > 0 && (
                      <div className="list-group mt-2" style={{maxHeight: '200px', overflowY: 'auto'}}>
                        {resultadosSubReceitas.map(ficha => (
                          <button
                            key={ficha.id}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onClick={() => adicionarSubReceita(ficha)}
                          >
                            <div className="d-flex justify-content-between">
                              <div>
                                <strong>{ficha.nome}</strong>
                                {ficha.categoria && <span className="badge bg-secondary ms-2">{ficha.categoria}</span>}
                              </div>
                              <span className="text-muted">{formatarMoeda(ficha.custo_unitario)}/{ficha.unidade_rendimento}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {subReceitas.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover align-middle">
                        <thead className="table-light">
                          <tr>
                            <th style={{width: '50%'}}>Preparação</th>
                            <th style={{width: '15%'}} className="text-center">Quantidade</th>
                            <th style={{width: '10%'}} className="text-center">Unidade</th>
                            <th style={{width: '10%'}} className="text-end">Custo Unit.</th>
                            <th style={{width: '10%'}} className="text-end">Subtotal</th>
                            <th style={{width: '5%'}}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {subReceitas.map(sub => (
                            <tr key={sub.id}>
                              <td><strong>{sub.nome}</strong></td>
                              
                              {/* QUANTIDADE */}
                              <td>
                                <input
                                  type="number"
                                  className="form-control form-control-sm text-center"
                                  value={sub.quantidade}
                                  onChange={(e) => atualizarSubReceita(sub.id, 'quantidade', e.target.value)}
                                  min="0"
                                  step="0.01"
                                />
                              </td>
                              
                              {/* UNIDADE - DROPDOWN */}
                              <td className="text-center">
                                <select
                                  className="form-select form-select-sm"
                                  value={sub.unidade_medida}
                                  onChange={(e) => atualizarSubReceita(sub.id, 'unidade_medida', e.target.value)}
                                >
                                  {sub.unidades_disponiveis?.map(u => (
                                    <option key={u.valor} value={u.valor}>{u.label}</option>
                                  ))}
                                </select>
                              </td>
                              
                              <td className="text-end">
                                <small>{formatarMoeda(sub.custo_unitario)}</small>
                              </td>
                              <td className="text-end">
                                <strong>{formatarMoeda(sub.custo_total)}</strong>
                              </td>
                              <td className="text-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => removerSubReceita(sub.id)}
                                >
                                  ×
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="table-warning fw-bold">
                            <td colSpan="4" className="text-end">Subtotal Preparações:</td>
                            <td className="text-end">
                              {formatarMoeda(subReceitas.reduce((sum, s) => sum + (s.custo_total || 0), 0))}
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div className="alert alert-secondary mb-0">
                      Nenhuma preparação base adicionada.
                    </div>
                  )}
                </div>
              </div>
              
            {/* Embalagens */}
            <div className="card mb-4">
              <div className="card-header bg-secondary text-white">
                <h5 className="mb-0">📦 Embalagens ({itens.filter(i => i.tipo === 'embalagem').length})</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Buscar Embalagem</label>
                  <input
                    type="text"
                    className="form-control"
                    value={buscaEmbalagem}
                    onChange={(e) => setBuscaEmbalagem(e.target.value)}
                    placeholder="Digite o nome da embalagem..."
                  />
                  
                  {resultadosEmbalagens.length > 0 && (
                    <div className="list-group mt-2" style={{maxHeight: '200px', overflowY: 'auto'}}>
                      {resultadosEmbalagens.map(embalagem => (
                        <button
                          key={embalagem.id}
                          type="button"
                          className="list-group-item list-group-item-action"
                          onClick={() => adicionarItem(embalagem, 'embalagem')}
                        >
                          <div className="d-flex justify-content-between">
                            <span>{embalagem.nome}</span>
                            <span className="text-muted">{formatarMoeda(embalagem.custo_medio)}/unidade</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {itens.filter(i => i.tipo === 'embalagem').length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle">
                      <thead className="table-light">
                        <tr>
                          <th style={{width: '50%'}}>Embalagem</th>
                          <th style={{width: '15%'}} className="text-center">Quantidade</th>
                          <th style={{width: '15%'}} className="text-end">Custo Unit.</th>
                          <th style={{width: '15%'}} className="text-end">Subtotal</th>
                          <th style={{width: '5%'}}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {itens.filter(i => i.tipo === 'embalagem').map(item => (
                          <tr key={item.id}>
                            {/* ✅ COLUNA DE NOME */}
                            <td><strong>{item.nome}</strong></td>
                            <td>
                              <input
                                type="number"
                                className="form-control form-control-sm text-center"
                                value={item.quantidade}
                                onChange={(e) => atualizarItem(item.id, 'quantidade', e.target.value)}
                                min="0"
                                step="1"
                              />
                            </td>
                            <td className="text-end">
                              <small>{formatarMoeda(item.custo_unitario)}</small>
                            </td>
                            <td className="text-end">
                              <strong>{formatarMoeda(item.custo_total)}</strong>
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => removerItem(item.id)}
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-light fw-bold">
                          <td colSpan="3" className="text-end">Subtotal Embalagens:</td>
                          <td className="text-end text-secondary">
                            {formatarMoeda(itens.filter(i => i.tipo === 'embalagem').reduce((sum, i) => sum + (i.custo_total || 0), 0))}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="alert alert-info mb-0">
                    Nenhuma embalagem adicionada.
                  </div>
                )}
              </div>
            </div>

            {/* Precificação - SOMENTE PARA PRODUTO FINAL */}
            {tipoFicha === 'produto_final' && (
              <div className="card mb-4 border-success">
                <div className="card-header bg-success text-white">
                  <h5 className="mb-0">💰 Precificação</h5>
                </div>
                <div className="card-body">
                  {/* Badges de Custos em uma linha */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-3">
                      <div className="p-3 bg-light rounded text-center">
                        <small className="text-muted d-block">Custo Total</small>
                        <h5 className="text-primary mb-0">{formatarMoeda(custoTotal)}</h5>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="p-3 bg-light rounded text-center">
                        <small className="text-muted d-block">Custo por {unidadeRendimento}</small>
                        <h5 className="text-info mb-0">{formatarMoeda(custoUnitario)}</h5>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="p-3 bg-light rounded text-center">
                        <small className="text-muted d-block">Preço de Venda</small>
                        <h5 className="text-success mb-0">{formatarMoeda(precoVenda)}</h5>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="p-3 bg-light rounded text-center">
                        <small className="text-muted d-block">Lucro por {unidadeRendimento}</small>
                        <h5 className="text-warning mb-0">
                          {formatarMoeda(
                            (parseFloat(precoVenda) || 0) - custoUnitario - 
                            (excluirEmbalagensCMV ? (custoEmbalagens / (parseFloat(quantidadePorcoes) || 1)) : 0)
                          )}
                        </h5>
                      </div>
                    </div>
                  </div>

                  <div className="row g-4">
                    {/* Preço de Venda */}
                    <div className="col-md-4">
                      <label className="form-label">Preço de Venda (R$)</label>
                      <div className="input-group">
                        <input
                          type="number"
                          className="form-control"
                          value={precoVenda}
                          onChange={(e) => handlePrecoVendaChange(e.target.value)}
                          onBlur={(e) => {
                            // Formata com 2 casas ao sair do campo
                            if (e.target.value) {
                              const valorFormatado = parseFloat(e.target.value).toFixed(2);
                              setPrecoVenda(valorFormatado);
                            }
                          }}
                          min="0"
                          step="0.01"
                          placeholder="0,00"
                        />
                        <span className="input-group-text bg-success text-white" style={{minWidth: '120px'}}>
                          <small>💡 Ideal: {formatarMoeda(getPrecoIdeal())}</small>
                        </span>
                      </div>
                    </div>

                    {/* CMV % */}
                    <div className="col-md-4">
                      <label className="form-label">CMV (%)</label>
                      <div className="input-group">
                        <input
                          type="number"
                          className="form-control"
                          value={cmvPercentual ? parseFloat(cmvPercentual).toFixed(2) : ''}  // ✅ SEMPRE 2 CASAS
                          onChange={(e) => handleCMVChange(e.target.value)}
                          min="1"
                          max="99"
                          step="0.01"
                          placeholder="30"
                        />
                        <span className={`input-group-text bg-${getCMVStatus().color} text-white`} style={{minWidth: '140px'}}>
                          <small>{getCMVStatus().text}</small>
                        </span>
                      </div>
                    </div>

                    {/* Margem de Lucro % */}
                    <div className="col-md-4">
                      <label className="form-label">Margem de Lucro (%)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={`${parseFloat(margemLucro).toFixed(2)}%`}
                        readOnly
                        style={{backgroundColor: '#f8f9fa'}}
                      />
                    </div>
                  </div>

                  {/* ✅ TOGGLE EMBALAGENS - NOVO */}
                  <hr className="my-4" />
                  
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="excluirEmbalagensCMV"
                      checked={excluirEmbalagensCMV}
                      onChange={(e) => setExcluirEmbalagensCMV(e.target.checked)}
                    />
                    <label className="form-check-label fw-bold" htmlFor="excluirEmbalagensCMV">
                      📦 Não incluir embalagens no CMV
                    </label>
                  </div>

                  {excluirEmbalagensCMV && custoEmbalagens > 0 && (
                    <div className="alert alert-info mb-3">
                      <strong>ℹ️ Modo: Embalagens Separadas</strong>
                      <ul className="mb-0 mt-2 small">
                        <li>Custo de insumos: {formatarMoeda(custoTotal)}</li>
                        <li>Custo de embalagens: {formatarMoeda(custoEmbalagens)} (não incluído no CMV)</li>
                        <li>CMV calculado apenas sobre insumos</li>
                        <li>Lucro = Preço de Venda - (Custo Ingredientes + Custo Embalagens)</li>
                      </ul>
                    </div>
                  )}

                  {/* Delivery - COM CHECKBOX */}
                  <hr className="my-4" />
                  
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="calcularDelivery"
                      checked={calcularDelivery}
                      onChange={(e) => setCalcularDelivery(e.target.checked)}
                    />
                    <label className="form-check-label fw-bold" htmlFor="calcularDelivery">
                      🚚 Calcular custos de delivery
                    </label>
                  </div>

                  {calcularDelivery && (
                    <div className="border rounded p-3 bg-light">
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Taxa de Delivery (%)</label>
                          <div className="input-group">
                            <input
                              type="number"
                              className="form-control"
                              value={taxaDelivery}
                              onChange={(e) => setTaxaDelivery(e.target.value)}
                              min="0"
                              max="100"
                              step="0.1"
                              placeholder="0"
                            />
                            <span className="input-group-text">%</span>
                          </div>
                          <small className="text-muted">Ex: 10% = Taxa de {formatarMoeda((parseFloat(precoVenda) || 0) * (parseFloat(taxaDelivery) || 0) / 100)}</small>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Como aplicar a taxa?</label>
                          <select
                            className="form-select"
                            value={modoDelivery}
                            onChange={(e) => setModoDelivery(e.target.value)}
                          >
                            <option value="incluir">Incluir no custo (recalcula preço mantendo CMV)</option>
                            <option value="adicionar">Adicionar ao preço final</option>
                          </select>
                        </div>

                        {parseFloat(taxaDelivery) > 0 && (
                          <div className="col-12">
                            <div className="alert alert-info mb-0">
                              <div className="row text-center">
                                <div className="col-4">
                                  <small className="text-muted d-block">Taxa em R$</small>
                                  <strong className="text-primary">
                                    {formatarMoeda((parseFloat(precoVenda) || 0) * (parseFloat(taxaDelivery) || 0) / 100)}
                                  </strong>
                                </div>
                                <div className="col-4">
                                  <small className="text-muted d-block">Custo {modoDelivery === 'incluir' ? 'c/' : 's/'} Delivery</small>
                                  <strong className={modoDelivery === 'incluir' ? 'text-danger' : ''}>
                                    {formatarMoeda(custoComDelivery)}
                                  </strong>
                                </div>
                                <div className="col-4">
                                  <small className="text-muted d-block">Preço Final</small>
                                  <strong className="text-success">
                                    {formatarMoeda(precoFinalComDelivery)}
                                  </strong>
                                </div>
                              </div>
                              <hr />
                              <small className="text-muted">
                                {modoDelivery === 'incluir' 
                                  ? `Taxa de ${taxaDelivery}% (${formatarMoeda(taxaDeliveryPorPorcao)}) incluída no custo. O preço é recalculado mantendo o CMV de ${parseFloat(cmvPercentual).toFixed(2)}%.`
                                  : `Taxa de ${taxaDelivery}% (${formatarMoeda(taxaDeliveryPorPorcao)}) adicionada ao preço unitário.`
                                }
                              </small>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Resumo de Custos - SOMENTE PARA PREPARAÇÃO BASE */}
            {tipoFicha === 'preparacao_base' && (
              <div className="card mb-4 border-dark">
                <div className="card-header bg-dark text-white">
                  <h5 className="mb-0">📊 Custo da Preparação</h5>
                </div>
                <div className="card-body">
                  <div className="row text-center g-3">
                    <div className="col-md-6">
                      <div className="p-4 bg-light rounded">
                        <small className="text-muted d-block">Custo Total</small>
                        <h3 className="text-primary mb-0">{formatarMoeda(custoTotal)}</h3>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="p-4 bg-light rounded">
                        <small className="text-muted d-block">Custo por {unidadeRendimento}</small>
                        <h3 className="text-info mb-0">{formatarMoeda(custoUnitario)}</h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modo de Preparo - NOVO */}
            <div className="card mb-4 border-info">
              <div className="card-header bg-info bg-opacity-10">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="checkModoPreparo"
                    checked={mostrarModoPreparo}
                    onChange={(e) => {
                      setMostrarModoPreparo(e.target.checked);
                      if (!e.target.checked) {
                        setModoPreparo('');
                      }
                    }}
                  />
                  <label className="form-check-label fw-bold" htmlFor="checkModoPreparo">
                    👨‍🍳 Adicionar Modo de Preparo
                  </label>
                </div>
              </div>
              
              {mostrarModoPreparo && (
                <div className="card-body">
                  <div className="alert alert-info mb-3">
                    <small>
                      💡 <strong>Dica:</strong> Descreva o passo a passo do preparo. 
                      Use numeração manual (1., 2., 3.) para melhor organização.
                    </small>
                  </div>
                  
                  <label className="form-label">Instruções de Preparo</label>
                  <textarea
                    className="form-control"
                    rows="8"
                    value={modoPreparo}
                    onChange={(e) => setModoPreparo(e.target.value)}
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      lineHeight: '1.6'
                    }}
                  />
                  
                  <small className="text-muted d-block mt-2">
                    📝 {modoPreparo.split('\n').filter(line => line.trim()).length} passos descritos
                  </small>
                </div>
              )}
            </div>

            {/* Botões de Ação */}
            <div className="d-flex gap-2 justify-content-end">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate('/fichas-tecnicas')}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary btn-lg"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : (
                  <>💾 {isEditMode ? 'Atualizar' : 'Criar'} Ficha Técnica</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FichaTecnicaForm;